import { useSEO } from "@/hooks/use-seo";
import { Link } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";
import { articleImages } from "@/data/article-images";
import { articles } from "@/data/articles";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ExpertContact from "@/components/ExpertContact";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { HeroBackground } from "@/components/HeroBackground";

export default function Articles() {
  const { language, localePath } = useLanguage();
  const nl = language === "nl";

  useSEO({
    title: nl
      ? "Artikelen | Wrkt Digital - Gratis inzichten voor D2C-merken die willen groeien"
      : "Articles | Wrkt Digital - Free insights for D2C brands ready to grow",
    description: nl
      ? "Praktische tips en strategieen voor D2C e-commerce groei. Van Meta Ads en TikTok tot data-driven branding en creative hooks. Gratis kennis van ons team, geen fluff. Start vandaag met onze gratis Growth Scan."
      : "Practical tips and strategies for D2C e-commerce growth. From Meta Ads and TikTok to data-driven branding and creative hooks. Free knowledge from our team, no fluff. Start today with our free Growth Scan.",
    path: "/artikelen",
    lang: nl ? "nl" : "en",
    keywords: nl
      ? "d2c marketing tips, meta ads strategie, tiktok ads e-commerce, data-driven branding, creative hooks, gratis growth scan, performance marketing blog, e-commerce groei tips, klaviyo tips, server-side tracking uitleg"
      : "d2c marketing tips, meta ads strategy, tiktok ads e-commerce, data-driven branding, creative hooks, free growth scan, performance marketing blog, e-commerce growth tips, klaviyo tips, server-side tracking explained",
  });

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(nl ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const sortedArticles = [...articles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <div className="min-h-screen bg-[#0A1F14] text-white font-sans">
      <Navbar activePage="artikelen" />


      <section className="relative pt-32 pb-16 overflow-hidden">
        <HeroBackground />
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <h3 className="text-[#4CEAAC] text-sm font-bold tracking-widest uppercase mb-4">
              {nl ? "Insights & Artikelen" : "Insights & Articles"}
            </h3>
            <h1 className="text-4xl md:text-6xl font-display font-bold leading-tight mb-6">
              {nl ? "Wat we denken, delen we." : "What we think, we share."}
            </h1>
            <p className="text-lg text-white/60 leading-relaxed max-w-2xl">
              {nl
                ? "Praktische inzichten, eerlijke meningen en lessen uit de praktijk. Over groei, advertising en alles wat D2C-merken bezighoudt."
                : "Thought leadership on D2C e-commerce, performance marketing and the latest developments in digital advertising. Written by our team."}
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sortedArticles.map((article, i) => (
              <motion.div
                key={article.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06, type: 'spring', stiffness: 120 }}
              >
                <Link href={localePath(`/artikelen/${article.slug}`)}>
                  <article
                    className="group bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden hover:border-[#4CEAAC]/30 hover:shadow-[0_0_30px_rgba(76,234,172,0.1)] transition-all duration-500 cursor-pointer h-full flex flex-col"
                    data-testid={`article-card-${article.slug}`}
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={articleImages[article.slug]}
                        alt={nl ? article.title : article.titleEn}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14] via-transparent to-transparent opacity-60"></div>
                    </div>

                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {(nl ? article.tags : article.tagsEn).map((tag) => (
                          <span
                            key={tag}
                            className="text-[10px] font-bold uppercase tracking-widest text-[#4CEAAC]/70 bg-[#4CEAAC]/10 px-2 py-0.5 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      <h2 className="text-lg font-display font-bold leading-snug mb-3 group-hover:text-[#4CEAAC] transition-colors duration-300">
                        {nl ? article.title : article.titleEn}
                      </h2>

                      <p className="text-sm text-white/50 leading-relaxed mb-4 flex-1">
                        {nl ? article.excerpt : article.excerptEn}
                      </p>

                      <div className="flex items-center justify-between text-xs text-white/30 pt-4 border-t border-white/5">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(article.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {article.readTime} min
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-[#4CEAAC] group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </div>
                  </article>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ExpertContact />

      <Footer />
    </div>
  );
}
